/**
 * authStorage.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Email-keyed token storage.
 *
 * PROBLEM it solves:
 *   When a user is deleted and re-created, their new account gets a new Django
 *   token. If tokens are stored under generic keys ('authToken', 'token') the
 *   old token from the deleted account lingers in localStorage alongside the
 *   new one. AuthContext then restores the ghost user, causing duplicate API
 *   requests — one per surviving token.
 *
 * SOLUTION:
 *   Store the active session under ONE canonical key: 'authToken'.
 *   On every login, call saveAuth(token, user) which:
 *     1. Clears ALL previous auth keys (no ghost tokens can survive)
 *     2. Writes the new token + user under the generic key so all existing
 *        getToken() / localStorage.getItem('authToken') calls keep working
 *        with zero changes elsewhere.
 *
 *   The email is stored separately so getStoredEmail() can be used as a
 *   stable React dependency instead of the full user object.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const TOKEN_KEY    = 'authToken';
const TOKEN_ALT    = 'token';       // legacy alias — kept for compatibility
const USER_KEY     = 'user';
const EMAIL_KEY    = 'authEmail';   // stable primitive dep for useEffect

/**
 * Save a new auth session.
 * Clears every previous auth-related key first so ghost tokens cannot survive
 * a delete-and-recreate cycle.
 *
 * @param {string} token  - Django Token key
 * @param {object} user   - User object from the API response
 */
export function saveAuth(token, user) {
  try {
    // ── 1. Wipe every known auth key so no ghost data survives ────────────────
    clearAuth();

    // ── 2. Write fresh session ────────────────────────────────────────────────
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(TOKEN_ALT, token);   // legacy readers keep working
    }
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      // Store email as a plain string for stable useEffect dependencies
      const email = user.email || user.identifier || '';
      if (email) localStorage.setItem(EMAIL_KEY, email);
    }
  } catch (e) {
    console.warn('authStorage.saveAuth error:', e);
  }
}

/**
 * Clear all auth data from localStorage.
 * Call on logout OR before saving a new session.
 */
export function clearAuth() {
  try {
    [TOKEN_KEY, TOKEN_ALT, USER_KEY, EMAIL_KEY].forEach(k => {
      localStorage.removeItem(k);
    });
  } catch (e) {
    console.warn('authStorage.clearAuth error:', e);
  }
}

/**
 * Get the stored token.
 * Falls back through both key names for backwards compatibility.
 * @returns {string}
 */
export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY)
        || localStorage.getItem(TOKEN_ALT)
        || '';
  } catch { return ''; }
}

/**
 * Get the stored user object.
 * @returns {object|null}
 */
export function getStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/**
 * Get the stored email as a plain string.
 * Safe to use directly as a React useEffect / useCallback dependency
 * because it's a primitive — unlike the full user object which is a
 * new reference on every parse.
 * @returns {string}
 */
export function getStoredEmail() {
  try {
    return localStorage.getItem(EMAIL_KEY) || '';
  } catch { return ''; }
}

/**
 * Shared badge-fetch debounce.
 * Any component that fetches badge/inbox counts calls this first.
 * If another component already fetched within the last 5 seconds,
 * returns false (skip). Otherwise stamps the time and returns true (go ahead).
 *
 * This prevents Header + ProfilePage from both firing on the same
 * window-focus / visibilitychange event when the user is on the profile page.
 *
 * @param {number} [windowMs=5000] — debounce window in milliseconds
 * @returns {boolean} true = safe to fetch, false = skip (too soon)
 */
export function shouldFetchBadge(windowMs = 5000) {
  try {
    const key  = '_lastBadgeFetch';
    const last = parseInt(localStorage.getItem(key) || '0', 10);
    const now  = Date.now();
    if (now - last < windowMs) return false; // another component fetched recently
    localStorage.setItem(key, String(now));
    return true;
  } catch { return true; } // if localStorage fails, allow the fetch
}

